"use client";

import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { Spinner } from "@timeghost/ui/shadcn/spinner";
import { cn } from "@timeghost/ui/utils";
import { cva, type VariantProps } from "class-variance-authority";
import { isValidElement, type ReactElement, type ReactNode } from "react";

const buttonVariants = cva(
	"group/button inline-flex shrink-0 select-none items-center justify-center whitespace-nowrap rounded-lg border border-transparent bg-clip-padding font-medium text-sm outline-none transition-all focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 active:not-aria-[haspopup]:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
	{
		variants: {
			variant: {
				default: "bg-primary/90 text-white/90 hover:bg-primary hover:text-white [a]:hover:bg-primary/80",
				/** Idle above card/popover; hover steps to input (dark muted==popover). */
				outline:
					"border-border bg-well text-accent-foreground hover:bg-input hover:text-foreground aria-expanded:bg-input aria-expanded:text-foreground dark:border-input",
				secondary:
					"bg-accent text-accent-foreground hover:bg-input hover:text-foreground aria-expanded:bg-input aria-expanded:text-foreground",
				ghost:
					"hover:bg-accent hover:text-accent-foreground aria-expanded:bg-accent aria-expanded:text-accent-foreground",
				empty:
					"border-border border-dashed bg-transparent text-muted-foreground hover:bg-accent hover:text-accent-foreground aria-expanded:bg-accent aria-expanded:text-accent-foreground",
				destructive:
					"bg-destructive/10 text-destructive hover:bg-destructive/20 focus-visible:border-destructive/40 focus-visible:ring-destructive/20 dark:bg-destructive/20 dark:focus-visible:ring-destructive/40 dark:hover:bg-destructive/30",
				link: "text-primary underline-offset-4 hover:underline",
			},
			size: {
				default: "h-9 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
				xs: "h-6 gap-1 in-data-[slot=button-group]:rounded-lg rounded-[min(var(--radius-md),10px)] px-2 text-xs has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
				sm: "h-8 gap-1.5 in-data-[slot=button-group]:rounded-lg rounded-[min(var(--radius-md),12px)] px-2.5 text-xs has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3.5",
				lg: "h-10 gap-1.5 px-3.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
				icon: "size-9",
				"icon-xs":
					"size-6 in-data-[slot=button-group]:rounded-lg rounded-[min(var(--radius-md),10px)] [&_svg:not([class*='size-'])]:size-3",
				"icon-sm": "size-8 in-data-[slot=button-group]:rounded-lg rounded-[min(var(--radius-md),12px)]",
				"icon-lg": "size-10",
				pill: "rounded-full px-3 py-2",
				"pill-sm": "rounded-full px-2.5 py-1.5",
				"pill-lg": "rounded-full px-3 py-2.5",
				"pill-xl": "rounded-full px-4 py-3",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
		},
	}
);

type ButtonSize = NonNullable<VariantProps<typeof buttonVariants>["size"]>;

const spinnerSizeForButton = (size: ButtonSize | null | undefined): "xs" | "sm" | "default" => {
	if (size === "xs" || size === "sm" || size === "icon-xs" || size === "icon-sm") {
		return "xs";
	}
	if (size === "lg" || size === "icon-lg") {
		return "default";
	}
	return "sm";
};

type ButtonProps = ButtonPrimitive.Props &
	VariantProps<typeof buttonVariants> & {
		readonly loading?: boolean;
		/** When set (including null/empty), replaces children while loading. Omit to keep label. */
		readonly loadingText?: ReactNode;
		readonly leadingIcon?: ReactNode;
		readonly trailingIcon?: ReactNode;
		/** Radix-style: child element becomes host (`render`). Prefer `render={<Link />}` for new code. */
		readonly asChild?: boolean;
	};

function childHostLabel(host: ReactElement): ReactNode {
	const hostProps = host.props as { children?: ReactNode };
	return hostProps.children;
}

function Button({
	variant = "default",
	size = "default",
	loading = false,
	loadingText,
	leadingIcon,
	trailingIcon,
	asChild = false,
	className,
	disabled,
	children,
	render,
	nativeButton,
	...props
}: ButtonProps) {
	const showLoadingText = loading && loadingText !== undefined;
	const childHost = asChild && isValidElement(children) ? children : undefined;
	const resolvedRender = render ?? childHost;
	const label = childHost ? childHostLabel(childHost) : children;

	return (
		<ButtonPrimitive
			{...props}
			aria-busy={loading || undefined}
			className={cn(buttonVariants({ variant, size, className }))}
			data-slot="button"
			disabled={disabled || loading}
			nativeButton={nativeButton ?? resolvedRender == null}
			render={resolvedRender}>
			{loading ? (
				<Spinner className="mx-0.5" color="current" data-icon="inline-start" size={spinnerSizeForButton(size)} />
			) : null}
			{leadingIcon && !loading ? leadingIcon : null}
			{showLoadingText ? loadingText : label}
			{trailingIcon && !loading ? trailingIcon : null}
		</ButtonPrimitive>
	);
}

export type { ButtonProps };
export { Button, buttonVariants };
